import {
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { IMaskInput } from 'react-imask';
import {
  BulletinResponse,
  CreateBulletinInput,
  UpdateBulletinInput,
} from 'services/interfaces/installation/bulletin';
import { useAppTabContext } from '../../../AppContext';
import { useBulletinContext } from '../BulletinContext';

const phoneMasks = [
  { mask: '(00) 0000-0000', maxLength: 10 },
  { mask: '(00) 00000-0000', maxLength: 11 },
];

interface BulletinFormModalProps {
  isOpen: boolean;
  bulletin: BulletinResponse | null;
  onClose(): void;
}

const BulletinFormModal: React.FC<BulletinFormModalProps> = ({
  isOpen,
  bulletin,
  onClose,
}) => {
  const { app } = useAppTabContext();
  const { loading, handleCreate, handleUpdate } = useBulletinContext();

  const isEditing = !!bulletin;

  const [ownerName, setOwnerName] = useState('');
  const [phone, setPhone] = useState('');

  const stripCountryCode = (value: string) => value.replace(/^\+55/, '');

  const formatPhoneToSend = (value: string) => `+55${value.replace(/\D/g, '')}`;

  useEffect(() => {
    if (isOpen) {
      setOwnerName(bulletin?.ownerName ?? '');
      setPhone(stripCountryCode(bulletin?.phone ?? ''));
    }
  }, [isOpen, bulletin]);

  const onSubmit = () => {
    const formattedPhone = formatPhoneToSend(phone);

    if (isEditing) {
      const payload: UpdateBulletinInput = {
        ownerName,
        phone: formattedPhone,
      };
      handleUpdate(bulletin.id, payload);
    } else {
      const payload: CreateBulletinInput = {
        idi: app.idi,
        ownerName,
        phone: formattedPhone,
      };
      handleCreate(payload);
    }
  };

  const phoneDigits = phone.replace(/\D/g, '');
  const isFormValid =
    ownerName.trim() !== '' &&
    (phoneDigits.length === 10 || phoneDigits.length === 11);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={!loading}
      closeOnEsc={!loading}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton isDisabled={loading} />
        <ModalHeader>
          {isEditing ? 'Editar boletim' : 'Novo boletim'}
        </ModalHeader>

        <ModalBody as={Stack} gap="1rem">
          <FormControl isRequired>
            <FormLabel fontSize="0.8rem" color="gray.500">
              Nome do responsável
            </FormLabel>
            <Input
              isDisabled={loading}
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder="Nome do responsável"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="0.8rem" color="gray.500">
              Telefone
            </FormLabel>
            <InputGroup>
              <InputLeftAddon>+55</InputLeftAddon>
              <Input
                as={IMaskInput}
                mask={phoneMasks}
                value={phone}
                onAccept={(value: string) => setPhone(value)}
                isDisabled={loading}
                placeholder="(27) 99999-9999"
                type="text"
              />
            </InputGroup>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr="3" onClick={onClose} isDisabled={loading}>
            Cancelar
          </Button>
          <Button
            colorScheme="yooga"
            isLoading={loading}
            isDisabled={!isFormValid}
            onClick={onSubmit}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulletinFormModal;
